import { useState, useRef, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useFadeIn } from "@/hooks/useFadeIn";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { supabase } from "@/integrations/supabase/client";
import type { WPHomepageFields } from "@/lib/wordpress";

const GOOGLE_REVIEW_URL = "https://maps.app.goo.gl/5FDTMaUJMwuAkND87";
const GOOGLE_RATING = 4.8;
const GOOGLE_REVIEW_COUNT = 17;

export interface TestimonialsSectionItem {
  name: string;
  text: string;
  avatar?: string;
}

// ─── Default (hardcoded) testimonials ───
const DEFAULT_TESTIMONIALS: TestimonialsSectionItem[] = [
  {
    name: "William Geller",
    text: "It is quite easy to recommend Hadassah Homes and Divine Design for their professionalism, coordination, creativity and commitment to stay within the budget they provided on a recent total rehab of a condominium project. Their work exceeded my expectations and as an absentee owner, all the work was completed within the time frame projected. Great job and kudos.",
  },
  {
    name: "Isaac C McDaniel",
    text: "Having worked in real estate for years buying and selling homes, I have worked with many professionals. Hadassah is one I confidently recommend. They are practical, intelligent, and affordable. They have a vision and an eye for detail.",
  },
  {
    name: "Betsy Encarnacion",
    text: "Hadassah Homes completely understood the assignment and brought our vision to life. Beyond being professional and punctual, they were genuinely fun to work with. Communication was excellent and they helped us stay within budget.",
  },
  {
    name: "Jean Ronald Ogando",
    text: "I called Hadassah Homes when I was buying my house. A year later I contacted them again and they remembered me immediately. They had only 14 days to remodel the house and worked day and night to finish the project. I was amazed at the results. They truly made the house feel like home.",
  },
  {
    name: "Emmanuel Gonzalez",
    text: "I have worked with this business on numerous occasions. They are professional, punctual, and consistently deliver great work. They listen carefully to the client's needs and provide excellent design guidance.",
  },
  {
    name: "Christian Gonzalez",
    text: "The service I received was top of the line. I loved the design options they offered and the creativity they bring to their projects. I would definitely recommend them if you want to transform your home.",
  },
];

const LINE_CLAMP_CHARS = 180;

const GoogleGIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="#EA4335"
      d="M12 10.2v3.9h5.4c-.2 1.2-.9 2.3-1.9 3v2.5h3.1c1.8-1.7 2.9-4.2 2.9-7.2 0-.7-.1-1.4-.2-2.1H12z"
    />
    <path
      fill="#34A853"
      d="M12 21c2.6 0 4.8-.9 6.4-2.4l-3.1-2.5c-.9.6-2 .9-3.3.9-2.5 0-4.6-1.7-5.4-4H3.4v2.6C5 18.8 8.2 21 12 21z"
    />
    <path
      fill="#4A90E2"
      d="M6.6 13c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V6.4H3.4C2.5 8.1 2 10 2 11.9c0 1.9.5 3.8 1.4 5.5L6.6 13z"
    />
    <path
      fill="#FBBC05"
      d="M12 5.1c1.4 0 2.7.5 3.7 1.4l2.8-2.8C16.8 2.1 14.6 1 12 1 8.2 1 5 3.2 3.4 6.4L6.6 9c.8-2.3 2.9-3.9 5.4-3.9z"
    />
  </svg>
);

const RatingStars = () => (
  <div className="flex items-center gap-1" aria-label="4.8 out of 5 stars">
    {Array.from({ length: 4 }).map((_, i) => (
      <Star key={i} className="h-3.5 w-3.5 fill-accent text-accent" strokeWidth={1.1} />
    ))}
    <div className="relative h-3.5 w-3.5">
      <Star className="absolute inset-0 h-3.5 w-3.5 text-accent/35" strokeWidth={1.1} />
      <div className="absolute inset-y-0 left-0 w-[80%] overflow-hidden">
        <Star className="h-3.5 w-3.5 fill-accent text-accent" strokeWidth={1.1} />
      </div>
    </div>
  </div>
);

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const TestimonialCard = ({
  name,
  text,
  avatar,
  sourceLabel = "Google review",
}: {
  name: string;
  text: string;
  avatar?: string;
  sourceLabel?: string;
}) => {
  const isLong = text.length > LINE_CLAMP_CHARS;
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
  const { t } = useLanguage();

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [expanded, text]);

  return (
    <div
      className="flex h-full flex-col border border-border/80 bg-card p-8 md:p-9
        transition-all duration-500 ease-out
        hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_hsl(var(--accent)/0.1)] hover:border-accent/35"
    >
      <div className="mb-5 flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className="w-4 h-4 text-accent fill-accent" strokeWidth={1} />
        ))}
      </div>

      <div
        ref={contentRef}
        className="mb-6 flex-1 overflow-hidden transition-[max-height] duration-500 ease-in-out"
        style={{ maxHeight: expanded ? `${contentHeight}px` : "5.8em" }}
      >
        <p className="text-[1rem] leading-[1.8] text-muted-foreground">
          &ldquo;{text}&rdquo;
        </p>
      </div>

      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mb-5 self-start text-sm font-medium text-accent/90 underline underline-offset-4 transition-colors duration-200 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        >
          {expanded ? t(translations.testimonials.showLess) : t(translations.testimonials.readMore)}
        </button>
      )}

      <div className="mt-auto border-t border-border/80 pt-5">
        <div className="flex items-center gap-3.5">
          {avatar ? (
            <img
              src={avatar}
              alt={name}
              className="h-11 w-11 shrink-0 rounded-full object-cover md:h-12 md:w-12"
              loading="lazy"
            />
          ) : (
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/12 font-body text-sm font-semibold uppercase tracking-[0.08em] text-accent md:h-12 md:w-12">
              {getInitials(name)}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-headline text-[1.05rem] font-semibold text-foreground">
              {name}
            </p>
          <p className="mt-0.5 text-[11px] text-neutral-500">{sourceLabel}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const useCarousel = (total: number, perView: number, autoplayMs = 5500) => {
  const maxIndex = Math.max(0, Math.ceil(total / perView) - 1);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const go = useCallback(
    (dir: 1 | -1) =>
      setIndex((prev) => {
        const next = prev + dir;
        if (next < 0) return maxIndex;
        if (next > maxIndex) return 0;
        return next;
      }),
    [maxIndex]
  );

  const goTo = useCallback((i: number) => setIndex(i), []);

  useEffect(() => {
    if (paused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => go(1), autoplayMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [paused, go, autoplayMs]);

  useEffect(() => {
    setIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  return { index, maxIndex, go, goTo, setPaused, dotCount: maxIndex + 1 };
};

const useSwipe = (onLeft: () => void, onRight: () => void) => {
  const startX = useRef(0);
  return {
    onTouchStart: (e: React.TouchEvent) => {
      startX.current = e.touches[0].clientX;
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const diff = e.changedTouches[0].clientX - startX.current;
      if (Math.abs(diff) > 50) {
        diff < 0 ? onLeft() : onRight();
      }
    },
  };
};

const usePerView = () => {
  const [perView, setPerView] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches ? 2 : 1
  );

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setPerView(e.matches ? 2 : 1);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return perView;
};

interface TestimonialsSectionProps {
  wp?: WPHomepageFields | null;
  title?: string;
  sectionId?: string;
  testimonials?: TestimonialsSectionItem[];
  pauseOnHover?: boolean;
  showGoogleSummary?: boolean;
  reviewSourceLabel?: string;
}

const TestimonialsSection = ({
  wp,
  title = "What Our Clients Say",
  sectionId = "testimonials",
  testimonials: testimonialsProp,
  pauseOnHover = true,
  showGoogleSummary = true,
  reviewSourceLabel = "Google review",
}: TestimonialsSectionProps = {}) => {
  const { ref, visible } = useFadeIn();
  const { t } = useLanguage();
  const [supabaseTestimonials, setSupabaseTestimonials] = useState<TestimonialsSectionItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("testimonials")
          .select("client_name, testimonial, image_url, sort_order, is_featured")
          .eq("is_published", true)
          .order("is_featured", { ascending: false })
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false });
        if (cancelled) return;
        if (error || !data || data.length === 0) return;
        const mapped: TestimonialsSectionItem[] = data.map((d) => ({
          name: d.client_name,
          text: d.testimonial,
          avatar: d.image_url ?? undefined,
        }));
        setSupabaseTestimonials(mapped);
      } catch {
        // silent fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const testimonials = testimonialsProp?.length
    ? testimonialsProp
    : supabaseTestimonials && supabaseTestimonials.length
    ? supabaseTestimonials
    : wp?.testimonials?.length
    ? wp.testimonials
        .filter((t) => t.quote && t.author)
        .map((t): TestimonialsSectionItem => ({ name: t.author!, text: t.quote! }))
    : DEFAULT_TESTIMONIALS;

  const perView = usePerView();
  const { index, go, goTo, setPaused, dotCount } = useCarousel(
    testimonials.length,
    perView
  );
  const swipeHandlers = useSwipe(
    () => go(1),
    () => go(-1)
  );

  return (
    <section id={sectionId} className="overflow-x-clip bg-[#f7f4ee] py-12 md:py-16">
      <div
        ref={ref}
        className={`mx-auto max-w-wide px-5 md:px-12 transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="mx-auto max-w-2xl text-center">
          <p className="mb-2 font-body text-[10px] uppercase tracking-[0.24em] text-accent">
            Client Confidence
          </p>
          <h2 className="mb-3 font-headline text-4xl leading-[1.08] text-foreground md:text-[3.9rem]">
            {title}
          </h2>
          <p className="text-sm text-neutral-600 md:text-base">
            Real feedback from clients across Massachusetts.
          </p>
          {showGoogleSummary && (
            <div className="mt-4 mb-10">
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noreferrer"
                className="mx-auto flex w-fit items-center justify-center gap-2.5 border border-border/70 bg-white/80 px-4 py-2.5 text-sm transition-colors duration-200 hover:border-accent/40 hover:bg-white"
              >
                <GoogleGIcon className="h-4 w-4 shrink-0 opacity-90" />
                <RatingStars />
                <span className="font-semibold text-foreground">
                  {GOOGLE_RATING} out of 5
                </span>
                <span className="text-sm text-neutral-500">·</span>
                <span className="text-sm text-neutral-500">{GOOGLE_REVIEW_COUNT} Google reviews</span>
              </a>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex text-sm text-neutral-500 underline underline-offset-4 transition-colors duration-200 hover:text-accent"
              >
                View on Google
              </a>
            </div>
          )}
        </div>
      </div>

      <div
        className="relative mx-auto max-w-wide px-5 md:px-12"
        onMouseEnter={() => pauseOnHover && setPaused(true)}
        onMouseLeave={() => pauseOnHover && setPaused(false)}
        {...swipeHandlers}
      >
        <button
          onClick={() => go(-1)}
          aria-label="Previous testimonials"
          className="absolute left-0 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center border border-border bg-card/95 text-foreground transition-all duration-300 hover:border-accent/50 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background md:flex"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>

        <button
          onClick={() => go(1)}
          aria-label="Next testimonials"
          className="absolute right-0 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center border border-border bg-card/95 text-foreground transition-all duration-300 hover:border-accent/50 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background md:flex"
        >
          <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        </button>

        <div className="overflow-hidden pt-2 md:px-12">
          <div
            className="flex will-change-transform transition-transform duration-[600ms] ease-[cubic-bezier(0.25,0.1,0.25,1)]"
            style={{
              transform: `translate3d(-${index * 100}%, 0, 0)`,
            }}
          >
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="shrink-0 px-3 md:px-4"
                style={{ width: `${100 / perView}%` }}
              >
                <TestimonialCard
                  name={t.name}
                  text={t.text}
                  avatar={t.avatar}
                  sourceLabel={reviewSourceLabel}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto mt-6 flex max-w-wide justify-center px-5 md:mt-8 md:px-12">
        <div className="flex items-center justify-center gap-2">
          {Array.from({ length: dotCount }).map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                i === index
                  ? "text-accent"
                  : "text-border hover:text-accent/40"
              }`}
            >
              <span
                className={`block rounded-full transition-all duration-400 ${
                  i === index ? "h-2 w-6 bg-accent" : "h-2.5 w-2.5 bg-current"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
